type TweenVars = Record<string, any>

// gsap is loaded on demand so it stays out of the entry bundle
let gsapPromise: Promise<typeof import('gsap').gsap> | undefined
const loadGsap = () => (gsapPromise ??= import('gsap').then(m => m.gsap))

const setInitial = (el: HTMLElement, initial: TweenVars) => {
  // hide immediately so nothing flashes while gsap loads
  if ('opacity' in initial) el.style.opacity = String(initial.opacity)
  loadGsap().then(gsap => gsap.set(el, initial))
}

export default defineNuxtPlugin((nuxtApp) => {
  const defaultOptions = { threshold: 0.2 }

  const parseDelay = (el: HTMLElement) => {
    const raw = el.getAttribute('delay')
    const num = raw ? Number(raw) : 0
    return isNaN(num) ? 0 : num / 1000
  }

  const observeWithReset = (
    el: HTMLElement,
    initial: TweenVars,
    animate: TweenVars,
    options: { once?: boolean } = {},
  ) => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const delay = parseDelay(el)
        loadGsap().then(gsap => gsap.to(el, { ...animate, delay }))
      }
      else if (!options.once) {
        setInitial(el, initial)
      }
    }, defaultOptions)

    observer.observe(el)
    return observer
  }

  const getPropValue = (props: any, key: string) => {
    const prop = props?.[`:${key}`] ?? props?.[key]
    if (typeof prop === 'string') {
      try {
        return JSON.parse(prop)
      }
      catch {
        return prop
      }
    }
    return prop
  }

  nuxtApp.vueApp.directive('motion', {
    beforeMount(el, _, vnode) {
      const initial = getPropValue(vnode.props, 'initial') || {}
      setInitial(el, initial)
    },
    mounted(el, _, vnode) {
      const props = vnode.props || {}
      const initial = getPropValue(props, 'initial') || {}
      const visible = getPropValue(props, 'visible') || {}
      const once = getPropValue(props, 'once') || false

      const observer = observeWithReset(
        el,
        initial,
        {
          ...visible,
          duration: 0.8,
          ease: 'elastic.out',
        },
        { once },
      )

      // save observer so we can unobserve later
      ;(el as any).__gsapObserver = observer
    },
    unmounted(el) {
      const observer = (el as any).__gsapObserver
      if (observer) observer.disconnect()
    },
    getSSRProps: () => ({}),
  })

  const directives = [
    {
      name: 'motion-slide-visible-bottom',
      initial: { opacity: 0, y: 50 },
      animate: { opacity: 1, y: 0, duration: 0.8, ease: 'elastic.out' },
    },
    {
      name: 'motion-roll-visible-once-right',
      initial: { opacity: 0, rotate: -25, x: -100 },
      animate: { opacity: 1, rotate: 0, x: 0, duration: 0.8, ease: 'elastic.out' },
      once: true,
    },
    {
      name: 'motion-pop-visible',
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1, duration: 0.8, ease: 'elastic.out' },
    },
  ]

  for (const d of directives) {
    nuxtApp.vueApp.directive(d.name, {
      beforeMount(el) {
        setInitial(el, d.initial)
      },
      mounted(el) {
        const observer = observeWithReset(el, d.initial, d.animate, { once: d.once ?? false })
        ;(el as any).__gsapObserver = observer
      },
      unmounted(el) {
        const observer = (el as any).__gsapObserver
        if (observer) observer.disconnect()
      },
      getSSRProps: () => ({}),
    })
  }
})
